import { useCallback, useEffect, useMemo, useState } from 'react';

const normalizePayload = (values = {}) => ({
  nombre: (values.nombre || '').trim(),
  descripcion: (values.descripcion || '').trim(),
  activo: Boolean(values.activo ?? true)
});

export const useAdminWarmers = (options = {}) => {
  const { activeOnly = false } = options;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const fetchList = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (activeOnly) {
        params.set('active_only', '1');
      }
      const query = params.toString();
      const response = await fetch(`/admin/api/warmers${query ? `?${query}` : ''}`, { signal });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener los warmers');
      }
      if (!signal?.aborted) {
        setItems(data.warmers || []);
      }
    } catch (err) {
      if (!signal?.aborted) {
        setError(err?.message || 'Error al cargar warmers');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [activeOnly]);

  useEffect(() => {
    const controller = new AbortController();
    fetchList(controller.signal);
    return () => controller.abort();
  }, [fetchList, refreshToken]);

  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  const saveWarmer = useCallback(async (values) => {
    const payload = normalizePayload(values);
    if (!payload.nombre) {
      throw new Error('El nombre del warmer es requerido.');
    }
    const hasId = Boolean(values?.id);
    const url = hasId ? `/admin/api/warmers/${values.id}` : '/admin/api/warmers';
    const method = hasId ? 'PUT' : 'POST';
    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo guardar el warmer');
    }
    return data;
  }, []);

  const deleteWarmer = useCallback(async (id) => {
    const response = await fetch(`/admin/api/warmers/${id}`, { method: 'DELETE' });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo eliminar el warmer');
    }
    return data;
  }, []);

  return useMemo(() => ({
    items,
    loading,
    error,
    refresh,
    saveWarmer,
    deleteWarmer
  }), [items, loading, error, refresh, saveWarmer, deleteWarmer]);
};
